// TurmasView — atalho admin pra gerenciar Turmas (grupos) sem sair do
// portal do aluno. Visível apenas para usuários com role admin de plataforma
// (gate em [c:\Dev\test-simulator\apps\student-new\src\App.tsx] via
// isPlatformAdmin.value).
//
// Reusa o endpoint /api/manager/groups que já existe pro manager-new — a
// API exige requireAdmin (platform_roles.role='admin'), então frontend não
// precisa duplicar lógica de auth.
//
// Diferenças vs GruposView do manager:
//   - UI mais enxuta: lista compacta + 2 modais quick-edit (members, bancos).
//   - Sem company filter / sem tags / sem owner picker — vão pra "Editar"
//     no modal completo se precisar (botão "Abrir no admin").
//   - Foco: matricular aluno e atribuir banco rápido.

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  Database,
  ExternalLink,
  GraduationCap,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  UsersRound,
  X
} from "lucide-react";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface GroupListRow {
  id: string;
  name: string;
  description: string | null;
  companyId: string | null;
  companyName: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  tags: string[];
  memberCount: number;
  bankCount: number;
  updatedAt: string;
}

interface GroupMember {
  id: string;
  email: string;
  name: string;
  apelido: string | null;
}

interface GroupBank {
  id: string;
  name: string;
}

interface GroupDetail extends GroupListRow {
  bankIds: string[];
  members: GroupMember[];
  banks: GroupBank[];
}

interface UserRow {
  id: string;
  email: string;
  name: string;
  apelido: string | null;
}

interface BankRow {
  id: string;
  name: string;
  companyName: string | null;
  description: string | null;
  questionCount: number;
  version: number;
  tags: string[];
  archivedAt: string | null;
}

async function call<T>(url: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {})
    }
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try {
      const body = (await r.json()) as { message?: string; error?: string };
      msg = body.message ?? body.error ?? msg;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

export function TurmasView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [groups, setGroups] = useState<GroupListRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [banks, setBanks] = useState<BankRow[]>([]);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingMembers, setEditingMembers] = useState<GroupDetail | null>(null);
  const [editingBanks, setEditingBanks] = useState<GroupDetail | null>(null);
  const [deleting, setDeleting] = useState<GroupListRow | null>(null);

  async function load() {
    setPhase("loading");
    setErrMsg(null);
    try {
      const [g, u, b] = await Promise.all([
        call<{ groups: GroupListRow[] }>("/api/manager/groups"),
        call<{ users: UserRow[] }>("/api/manager/users"),
        call<{ banks: BankRow[] }>("/api/manager/banks")
      ]);
      setGroups(g.groups);
      setUsers(u.users);
      setBanks(b.banks);
      setPhase("ready");
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function openMembersOf(id: string) {
    try {
      const res = await call<{ group: GroupDetail }>(
        `/api/manager/groups?id=${id}`
      );
      setEditingMembers(res.group);
    } catch (e) {
      alert(friendlyError(e));
    }
  }

  async function openBanksOf(id: string) {
    try {
      const res = await call<{ group: GroupDetail }>(
        `/api/manager/groups?id=${id}`
      );
      setEditingBanks(res.group);
    } catch (e) {
      alert(friendlyError(e));
    }
  }

  async function doDelete(g: GroupListRow) {
    if (!confirm(`Excluir turma "${g.name}"? Membros perdem acesso aos bancos via essa turma (grants individuais não são removidos).`)) {
      return;
    }
    try {
      await call(`/api/manager/groups?id=${g.id}`, { method: "DELETE" });
      setDeleting(null);
      void load();
    } catch (e) {
      alert(friendlyError(e));
    }
  }

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((g) =>
      `${g.name} ${g.description ?? ""} ${g.companyName ?? ""} ${g.ownerName ?? ""} ${g.tags.join(" ")}`
        .toLowerCase()
        .includes(term)
    );
  }, [groups, search]);

  if (phase === "loading") {
    return (
      <div class="space-y-3">
        <div class="h-12 animate-pulse rounded-md bg-muted/40" />
        <div class="h-16 animate-pulse rounded-md bg-muted/40" />
        <div class="h-16 animate-pulse rounded-md bg-muted/40" />
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
        <div class="flex items-start gap-2">
          <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
          <div>
            <h3 class="text-sm font-bold">Falha ao carregar turmas</h3>
            {errMsg && <p class="text-xs text-muted-foreground">{errMsg}</p>}
            <button
              type="button"
              onClick={() => void load()}
              class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-4 py-3 text-sm">
        <GraduationCap className="mr-1 inline size-4 text-violet-600 dark:text-violet-300" />
        <strong>Atalho admin.</strong> Aqui você gerencia turmas sem sair do
        portal do aluno. Toda matrícula nova é sincronizada com os bancos da
        turma automaticamente.{" "}
        <a
          href="/manager/"
          class="font-semibold text-violet-700 underline hover:text-violet-800 dark:text-violet-300"
        >
          Abrir gestão completa no /manager/
          <ExternalLink className="ml-0.5 inline size-3" />
        </a>
      </div>

      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Buscar turma…"
            value={search}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" />
          Nova turma
        </button>
      </div>

      {filtered.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-background/40 px-4 py-10 text-center">
          <UsersRound className="mx-auto mb-2 size-8 text-muted-foreground" />
          <p class="text-sm text-muted-foreground">
            {search ? "Nenhuma turma corresponde à busca." : "Nenhuma turma criada ainda."}
          </p>
        </div>
      ) : (
        <div class="overflow-x-auto rounded-md border border-border">
          <table class="w-full text-sm">
            <thead class="bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th class="px-3 py-2 text-left">Turma</th>
                <th class="px-3 py-2 text-left">Org / Dono</th>
                <th class="px-3 py-2 text-center">Membros</th>
                <th class="px-3 py-2 text-center">Bancos</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g.id} class="border-t border-border align-middle">
                  <td class="px-3 py-2">
                    <div class="font-semibold">{g.name}</div>
                    {g.description && (
                      <div class="text-[11px] text-muted-foreground line-clamp-1">
                        {g.description}
                      </div>
                    )}
                  </td>
                  <td class="px-3 py-2 text-[11px] text-muted-foreground">
                    {g.companyName ?? "Plataforma"}
                    {g.ownerName && <div>· {g.ownerName}</div>}
                  </td>
                  <td class="px-3 py-2 text-center font-mono text-xs">
                    {g.memberCount}
                  </td>
                  <td class="px-3 py-2 text-center font-mono text-xs">
                    {g.bankCount}
                  </td>
                  <td class="px-3 py-2 text-right">
                    <div class="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void openMembersOf(g.id)}
                        class="rounded-md border border-sky-500/40 bg-sky-500/10 p-1.5 text-sky-700 hover:bg-sky-500/20 dark:text-sky-300"
                        title="Matricular alunos"
                        aria-label="Matricular alunos"
                      >
                        <UsersRound width="0.9em" height="0.9em" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void openBanksOf(g.id)}
                        class="rounded-md border border-violet-500/40 bg-violet-500/10 p-1.5 text-violet-700 hover:bg-violet-500/20 dark:text-violet-300"
                        title="Bancos da turma"
                        aria-label="Bancos da turma"
                      >
                        <Database width="0.9em" height="0.9em" />
                      </button>
                      <a
                        href="/manager/"
                        title="Editar tudo no admin"
                        aria-label="Editar no admin"
                        class="rounded-md border border-border bg-background p-1.5 hover:bg-accent"
                      >
                        <Pencil width="0.9em" height="0.9em" />
                      </a>
                      <button
                        type="button"
                        onClick={() => void doDelete(g)}
                        class="rounded-md border border-destructive/30 bg-destructive/5 p-1.5 text-destructive hover:bg-destructive/10"
                        title="Excluir turma"
                        aria-label="Excluir turma"
                      >
                        <Trash2 width="0.9em" height="0.9em" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateTurmaModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
      {editingMembers && (
        <MembersModal
          group={editingMembers}
          users={users}
          onClose={() => {
            setEditingMembers(null);
            void load();
          }}
        />
      )}
      {editingBanks && (
        <BanksModal
          group={editingBanks}
          banks={banks}
          onClose={() => {
            setEditingBanks(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ---- Create modal (minimal) ----

function CreateTurmaModal({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      await call("/api/manager/groups", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          tags: [],
          bankIds: [],
          members: []
        })
      });
      onCreated();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm">
      <div class="w-full max-w-md rounded-md border border-border bg-card p-5 shadow-2xl">
        <header class="mb-4 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <GraduationCap width="1.2em" height="1.2em" className="text-primary" />
            Nova turma
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="space-y-3">
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Nome
            </label>
            <input
              value={name}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
              placeholder="Ex.: ENAMED 2026 - Turma A"
              autoFocus
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-muted-foreground">
              Descrição (opcional)
            </label>
            <textarea
              value={description}
              onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              rows={2}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          {err && (
            <p class="text-xs text-destructive">
              <AlertCircle className="mr-1 inline size-3" />
              {err}
            </p>
          )}
          <div class="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !name.trim()}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 width="0.9em" height="0.9em" className="animate-spin" />
              ) : (
                <Plus width="0.9em" height="0.9em" />
              )}
              Criar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Members modal (check-toggle live-save) ----

function MembersModal({
  group,
  users,
  onClose
}: {
  group: GroupDetail;
  users: UserRow[];
  onClose: () => void;
}) {
  const [memberIds, setMemberIds] = useState<string[]>(group.members.map((m) => m.id));
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const selectedSet = new Set(memberIds);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) {
      return [...users].sort((a, b) => {
        const aSel = selectedSet.has(a.id) ? 0 : 1;
        const bSel = selectedSet.has(b.id) ? 0 : 1;
        if (aSel !== bSel) return aSel - bSel;
        return a.name.localeCompare(b.name);
      });
    }
    return users
      .filter((u) =>
        `${u.email} ${u.name} ${u.apelido ?? ""}`.toLowerCase().includes(term)
      )
      .slice(0, 100);
  }, [users, q, memberIds]);

  async function toggle(userId: string) {
    const isMember = memberIds.includes(userId);
    setBusy(userId);
    try {
      await call(`/api/manager/groups?id=${group.id}`, {
        method: "PATCH",
        body: JSON.stringify(
          isMember ? { removeMembers: [userId] } : { addMembers: [userId] }
        )
      });
      // Após adicionar, sincroniza grants automaticamente (auto-sync).
      if (!isMember) {
        void call(
          `/api/manager/groups?id=${group.id}&action=sync-grants`,
          { method: "POST" }
        ).catch(() => { /* sync best-effort */ });
      }
      setMemberIds(isMember ? memberIds.filter((x) => x !== userId) : [...memberIds, userId]);
    } catch (e) {
      alert(friendlyError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Users width="1.2em" height="1.2em" className="text-primary" />
            Matricular alunos: {group.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">
          <div class="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{memberIds.length} matriculado{memberIds.length !== 1 ? "s" : ""}</span>
            <span>Cada clique salva e libera os bancos da turma.</span>
          </div>
          <div class="relative">
            <Search
              width="1em"
              height="1em"
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="search"
              value={q}
              onInput={(e) => setQ((e.target as HTMLInputElement).value)}
              placeholder="Buscar por email, nome…"
              class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </div>
          <div class="max-h-[400px] overflow-y-auto rounded-md border border-border bg-background">
            {filtered.length === 0 ? (
              <p class="p-3 text-center text-xs italic text-muted-foreground">
                Nenhum usuário encontrado.
              </p>
            ) : (
              <ul class="divide-y divide-border">
                {filtered.map((u) => {
                  const checked = selectedSet.has(u.id);
                  const isBusy = busy === u.id;
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => void toggle(u.id)}
                        disabled={isBusy}
                        class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors disabled:opacity-50 ${
                          checked
                            ? "bg-primary/10 hover:bg-primary/15"
                            : "hover:bg-accent/30"
                        }`}
                      >
                        <span
                          class={`inline-flex size-4 shrink-0 items-center justify-center rounded border ${
                            checked
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-background"
                          }`}
                        >
                          {checked && <Check width="0.75em" height="0.75em" />}
                        </span>
                        <span class="min-w-0 flex-1">
                          <span class="block truncate font-semibold">{u.name}</span>
                          <span class="block truncate font-mono text-[10px] text-muted-foreground">
                            {u.email}
                          </span>
                        </span>
                        {isBusy && (
                          <Loader2
                            width="0.9em"
                            height="0.9em"
                            className="animate-spin text-muted-foreground"
                          />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        <div class="border-t border-border bg-card px-4 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Banks modal (check-toggle live-save) ----

function BanksModal({
  group,
  banks,
  onClose
}: {
  group: GroupDetail;
  banks: BankRow[];
  onClose: () => void;
}) {
  const [bankIds, setBankIds] = useState<string[]>(group.bankIds);
  const [busy, setBusy] = useState<boolean>(false);
  const [q, setQ] = useState("");

  const selectedSet = new Set(bankIds);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = banks.filter((b) => !b.archivedAt);
    if (!term) return list.slice(0, 60);
    return list
      .filter((b) =>
        `${b.name} ${b.description ?? ""} ${b.companyName ?? ""} ${b.tags.join(" ")}`
          .toLowerCase()
          .includes(term)
      )
      .slice(0, 60);
  }, [banks, q]);

  async function toggle(id: string) {
    const isOn = bankIds.includes(id);
    const next = isOn ? bankIds.filter((x) => x !== id) : [...bankIds, id];
    setBusy(true);
    try {
      await call(`/api/manager/groups?id=${group.id}`, {
        method: "PATCH",
        body: JSON.stringify({ bankIds: next })
      });
      setBankIds(next);
      // Auto-sync grants pro novo banco (não bloqueia UI).
      if (!isOn) {
        void call(
          `/api/manager/groups?id=${group.id}&action=sync-grants`,
          { method: "POST" }
        ).catch(() => { /* best-effort */ });
      }
    } catch (e) {
      alert(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Database width="1.2em" height="1.2em" className="text-primary" />
            Bancos da turma: {group.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">
          <div class="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{bankIds.length} banco{bankIds.length !== 1 ? "s" : ""} atribuído{bankIds.length !== 1 ? "s" : ""}</span>
            <span>Membros recebem acesso automático.</span>
          </div>
          <div class="relative">
            <Search
              width="1em"
              height="1em"
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="search"
              value={q}
              onInput={(e) => setQ((e.target as HTMLInputElement).value)}
              placeholder="Buscar banco…"
              class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </div>
          <div class="max-h-[400px] overflow-y-auto rounded-md border border-border bg-background">
            {filtered.length === 0 ? (
              <p class="p-3 text-center text-xs italic text-muted-foreground">
                Nenhum banco encontrado.
              </p>
            ) : (
              <ul class="divide-y divide-border">
                {filtered.map((b) => {
                  const checked = selectedSet.has(b.id);
                  return (
                    <li key={b.id}>
                      <button
                        type="button"
                        onClick={() => void toggle(b.id)}
                        disabled={busy}
                        class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors disabled:opacity-50 ${
                          checked
                            ? "bg-primary/10 hover:bg-primary/15"
                            : "hover:bg-accent/30"
                        }`}
                      >
                        <span
                          class={`inline-flex size-4 shrink-0 items-center justify-center rounded border ${
                            checked
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-background"
                          }`}
                        >
                          {checked && <Check width="0.75em" height="0.75em" />}
                        </span>
                        <span class="min-w-0 flex-1">
                          <span class="block truncate font-semibold">{b.name}</span>
                          <span class="block truncate text-[10px] text-muted-foreground">
                            {b.companyName ?? "Sem company"} · {b.questionCount} questões
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        <div class="border-t border-border bg-card px-4 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
